import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';

// Administração → Histórico de acessos (só o superadmin): quem entrou e o que acessou.
// Telas abertas, logins e saídas registrados pelo Quality (com IP e aparelho) + logins/saídas/trocas de senha
// do servidor de login (desde 04/10/2026). Dados em api/acessos.
async function api(acao, dados = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(`/api/acessos?acao=${acao}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
    body: JSON.stringify(dados),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
  return j;
}
const hojeISO = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const diasAtras = n => new Date(Date.now() - n * 86400000 - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const quando = iso => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' }) : '—');
const TIPOS = { tela: { r: 'Tela', c: '#2563eb', f: '#dbeafe' }, login: { r: 'Login', c: '#166534', f: '#dcfce7' }, logout: { r: 'Saída', c: '#92400e', f: '#fef3c7' }, acao: { r: 'Ação', c: '#7c3aed', f: '#ede9fe' } };

const estilo = {
  pagina: { padding: 22, background: '#f4f7f6', minHeight: 'calc(100vh - 60px)', boxSizing: 'border-box' },
  card: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 16, marginBottom: 14 },
  input: { padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5 },
  btn: { padding: '8px 14px', borderRadius: 8, border: 0, background: '#00897b', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13.5 },
  btnClaro: { padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer', fontSize: 13.5 },
  th: { textAlign: 'left', fontSize: 11.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#f8fafc' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 13, verticalAlign: 'top' },
};

export default function HistoricoAcessos() {
  const [aba, setAba] = useState('resumo');
  const [resumo, setResumo] = useState(null);
  const [linhas, setLinhas] = useState(null);
  const [filtro, setFiltro] = useState({ email: '', de: diasAtras(7), ate: hojeISO(), tipo: '' });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [buscaResumo, setBuscaResumo] = useState('');

  useEffect(() => { api('resumo').then(r => setResumo(r.pessoas)).catch(e => setErro(e.message)); }, []);
  const buscar = useCallback(async (f = filtro) => {
    setCarregando(true); setErro('');
    try { const r = await api('listar', f); setLinhas(r.linhas); } catch (e) { setErro(e.message); } finally { setCarregando(false); }
  }, [filtro]);
  const verPessoa = email => { const f = { ...filtro, email, de: diasAtras(30) }; setFiltro(f); setAba('linha'); buscar(f); };

  const resumoFiltrado = useMemo(() => {
    const t = buscaResumo.trim().toLowerCase();
    return (resumo || []).filter(p => !t || p.email.includes(t) || (p.nome || '').toLowerCase().includes(t));
  }, [resumo, buscaResumo]);

  function exportarCsv() {
    const cab = ['data_hora', 'nome', 'email', 'tipo', 'o_que', 'caminho', 'ip', 'aparelho', 'origem'];
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cab.join(';'), ...(linhas || []).map(l => [quando(l.criado_em), l.nome, l.email, TIPOS[l.tipo]?.r || l.tipo, l.tela, l.caminho, l.ip, l.aparelho, l.origem].map(esc).join(';'))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `historico-acessos-${filtro.de}-a-${filtro.ate}.csv`;
    a.click();
  }

  return (
    <div style={estilo.pagina}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>🕵️ Histórico de acessos</h2>
          <div style={{ color: '#64748b', fontSize: 13 }}>Quem entrou no Quality e quais telas abriu. Logins desde 04/10/2026; telas, IP e aparelho desde 23/10/2026. Guardado por 180 dias.</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={aba === 'resumo' ? estilo.btn : estilo.btnClaro} onClick={() => setAba('resumo')}>Último acesso de cada pessoa</button>
          <button style={aba === 'linha' ? estilo.btn : estilo.btnClaro} onClick={() => { setAba('linha'); if (!linhas) buscar(); }}>Linha do tempo</button>
        </div>
      </div>
      {erro && <div style={{ ...estilo.card, background: '#fee2e2', color: '#7f1d1d', borderColor: '#fecaca' }}>{erro}</div>}

      {aba === 'resumo' && (
        <div style={estilo.card}>
          <input style={{ ...estilo.input, width: 300, marginBottom: 10 }} placeholder="Buscar pessoa (nome ou e-mail)" value={buscaResumo} onChange={e => setBuscaResumo(e.target.value)} />
          {!resumo ? <div style={{ color: '#64748b' }}>Carregando…</div> : (
            <div style={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Pessoa', 'Último acesso', 'Última tela', 'Último login', 'Logins (30 dias)', 'Telas (30 dias)', 'IP / aparelho', ''].map(h => <th key={h} style={estilo.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {resumoFiltrado.map(p => (
                    <tr key={p.email}>
                      <td style={estilo.td}><strong>{p.nome || '—'}</strong><div style={{ color: '#64748b', fontSize: 12 }}>{p.email}</div></td>
                      <td style={estilo.td}>{quando(p.ultimoAcesso)}</td>
                      <td style={estilo.td}>{p.ultimaTela || '—'}</td>
                      <td style={estilo.td}>{quando(p.ultimoLogin)}</td>
                      <td style={estilo.td}>{p.logins30d}</td>
                      <td style={estilo.td}>{p.telas30d}</td>
                      <td style={estilo.td}>{p.ip || '—'}<div style={{ color: '#64748b', fontSize: 12 }}>{p.aparelho}</div></td>
                      <td style={estilo.td}><button style={{ ...estilo.btnClaro, padding: '4px 10px', fontSize: 12 }} onClick={() => verPessoa(p.email)}>Ver tudo</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!resumoFiltrado.length && <div style={{ color: '#64748b', padding: 12 }}>Ninguém encontrado.</div>}
            </div>
          )}
        </div>
      )}

      {aba === 'linha' && (
        <div style={estilo.card}>
          <form onSubmit={e => { e.preventDefault(); buscar(); }} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <input list="hist-pessoas" style={{ ...estilo.input, width: 280 }} placeholder="E-mail da pessoa (vazio = todos)" value={filtro.email} onChange={e => setFiltro({ ...filtro, email: e.target.value })} />
            <datalist id="hist-pessoas">{(resumo || []).map(p => <option key={p.email} value={p.email}>{p.nome}</option>)}</datalist>
            <label style={{ fontSize: 13, color: '#475569' }}>de <input type="date" style={estilo.input} value={filtro.de} onChange={e => setFiltro({ ...filtro, de: e.target.value })} /></label>
            <label style={{ fontSize: 13, color: '#475569' }}>até <input type="date" style={estilo.input} value={filtro.ate} onChange={e => setFiltro({ ...filtro, ate: e.target.value })} /></label>
            <select style={estilo.input} value={filtro.tipo} onChange={e => setFiltro({ ...filtro, tipo: e.target.value })}>
              <option value="">Tudo</option><option value="tela">Telas abertas</option><option value="login">Logins</option><option value="logout">Saídas</option><option value="acao">Ações</option>
            </select>
            <button style={estilo.btn} disabled={carregando}>{carregando ? 'Buscando…' : 'Buscar'}</button>
            {linhas?.length > 0 && <button type="button" style={estilo.btnClaro} onClick={exportarCsv}>⬇ Exportar (Excel)</button>}
            {linhas && <span style={{ color: '#64748b', fontSize: 12.5 }}>{linhas.length.toLocaleString('pt-BR')} registro(s)</span>}
          </form>
          {linhas && (
            <div style={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Quando', 'Pessoa', 'Tipo', 'O que', 'IP', 'Aparelho', 'Origem'].map(h => <th key={h} style={estilo.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {linhas.map((l, i) => {
                    const t = TIPOS[l.tipo] || { r: l.tipo, c: '#334155', f: '#f1f5f9' };
                    return (
                      <tr key={i}>
                        <td style={{ ...estilo.td, whiteSpace: 'nowrap' }}>{quando(l.criado_em)}</td>
                        <td style={estilo.td}><strong>{l.nome || '—'}</strong><div style={{ color: '#64748b', fontSize: 12 }}>{l.email}</div></td>
                        <td style={estilo.td}><span style={{ background: t.f, color: t.c, borderRadius: 10, padding: '1px 8px', fontSize: 11.5, fontWeight: 700 }}>{t.r}</span></td>
                        <td style={estilo.td}>{l.tela || '—'}{l.caminho && l.tipo === 'tela' && <div style={{ color: '#94a3b8', fontSize: 11.5 }}>{l.caminho}</div>}</td>
                        <td style={estilo.td}>{l.ip || '—'}</td>
                        <td style={estilo.td}>{l.aparelho || '—'}</td>
                        <td style={{ ...estilo.td, color: '#64748b', fontSize: 12 }}>{l.origem}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!linhas.length && <div style={{ color: '#64748b', padding: 12 }}>Nada nesse período.</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
